import React, { useState, useRef } from "react";
import { WhatsappLogo } from "@phosphor-icons/react";

/**
 * Razorpay "Payment Button" embed.
 *
 * We do NOT auto-load Razorpay's script on mount, because:
 *  1. It triggers cross-origin "Script error." in CRA's dev overlay
 *     (harmless but noisy).
 *  2. It only authorizes the production domain — loading on preview
 *     is a wasted network request.
 *
 * Instead, we render a styled placeholder button. When the user clicks
 * it, we inject the Razorpay <script>, which replaces our form with
 * Razorpay's actual "Pay Now" button. The user then taps that to pay.
 *
 * On production (greateagle.co.in) the click-then-pay flow is one extra
 * tap; on preview, it gracefully degrades to "open WhatsApp" if the
 * script fails.
 */
export default function RazorpayButton({ buttonId, amount, packageLabel }) {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const formRef = useRef(null);

  // No hosted button (or new-pricing pending) → straight to WhatsApp UPI
  const noButton = !buttonId;

  const load = () => {
    if (loaded || !formRef.current || !buttonId) return;
    setLoaded(true);
    const form = formRef.current;
    form.innerHTML = "";
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/payment-button.js";
    s.async = true;
    s.setAttribute("data-payment_button_id", buttonId);
    s.onerror = (e) => {
      if (e && typeof e.preventDefault === "function") e.preventDefault();
      setFailed(true);
      // eslint-disable-next-line no-console
      console.warn("[Razorpay] could not load — falling back to WhatsApp");
    };
    form.appendChild(s);
  };

  if (failed || noButton) {
    return (
      <a
        href={`https://wa.me/918129387138?text=${encodeURIComponent(
          `Hi, I want to pay for the ${packageLabel} package (₹${amount.toLocaleString(
            "en-IN"
          )}) via UPI.`
        )}`}
        target="_top"
        rel="noopener noreferrer"
        data-testid={`razorpay-fallback-${buttonId}`}
        className="w-full inline-flex items-center justify-center gap-2 bg-[#25D366] text-[#0A0A0A] py-3.5 font-body text-sm uppercase tracking-[0.22em] font-bold border border-[#25D366]"
      >
        <WhatsappLogo size={16} weight="fill" />
        Pay via WhatsApp UPI
      </a>
    );
  }

  return (
    <div className="w-full">
      {!loaded && (
        <button
          type="button"
          data-testid={`razorpay-trigger-${buttonId}`}
          onClick={load}
          className="w-full inline-flex items-center justify-center gap-3 py-3.5 font-body text-sm uppercase tracking-[0.22em] bg-[#3399CC] text-white border border-[#3399CC] hover:bg-[#2680A6] transition-colors"
        >
          <span className="font-mono-acc font-bold text-base tracking-tight">
            Razorpay
          </span>
          <span>Pay via UPI / Cards</span>
        </button>
      )}
      <form
        ref={formRef}
        data-testid={`razorpay-${buttonId}`}
        className={`razorpay-embed flex justify-center ${loaded ? "mt-2" : "hidden"}`}
      />
    </div>
  );
}
